import {reactive, watch} from "vue";
import { apiHostname } from "config";

import { getDataWithoutLimit } from "helpers/_api";
import { initCache } from "helpers/_cache";

import { env } from "config";
import { portalStore, fetchRelated, resolveValueParts, usergroup_ids, isCompanyUndefinedAllowed } from "store/portal";
import { department } from "store/department";
import { company } from "store/company";

import { workflowlayout } from "store/workflow";

export const generalFilters = reactive({});

// Define cachable values and set initial cache
const cacheableValues = {
	workflowusergroup: null
};
Object.keys(cacheableValues).forEach((key) => {
	initCache("filters", key, generalFilters);
});


// Based on the module_access get related Workflowfilters
const getFilters = (module_access, id) => {
	return new Promise((resolve, reject) => {
		const filters = [
			[
				"FieldFilter",
				{
					"column": "module_object",
					"value": "Workflowfilter"
				}
			]
		];
		const tmpFilters = {
			data: []
		};
		getDataWithoutLimit(`${apiHostname}/api/v1.0/${module_access}/${id}/links`, filters, tmpFilters, "data").then(() => {
			resolve(tmpFilters.data);
		});
	});
};

// Store usergroup filters based on usergroup_ids
export const getUsergroupFilters = () => {
	const usergroupPromises = [];
	usergroup_ids.value.forEach(id => {
		// @TODO pass this as extra data instead, removing the extra API calls
		usergroupPromises.push(getFilters("workflowusergroup", id));
	});
	Promise.all(usergroupPromises).then(values => {
		const filtersByUsergroup = values.flat();
		generalFilters["workflowusergroup"] = JSON.parse(JSON.stringify(filtersByUsergroup));
	});
};

export const getFiltersAndStore = (module_access, id) => {
	return new Promise((resolve, reject) => {
		if (generalFilters[module_access] && generalFilters[module_access][id]) {
			// When filters already been resolved just return them, no need to reload
			return resolve(generalFilters[module_access][id]);
		}
		// Set the defaults and resolve
		!(module_access in generalFilters) && (generalFilters[module_access] = {});
		!(id in generalFilters[module_access]) && (generalFilters[module_access][id] = {});
		generalFilters[module_access][id] = undefined;
		resolve(generalFilters[module_access][id]);
		// Now get the data
		getFilters(module_access, id).then(result => {
			generalFilters[module_access][id] = resolveFilters(result);
		});
	});
};
// Resolve filters, return array with objects used to pass as body for calls
export const resolveFilters = (filterArray) => {
	const filters = [];
	filterArray.filter(a => typeof a !== 'undefined').forEach(filterData => {
		const targetData = filterData?.data ?? filterData;
		if (!targetData) return [];
		if (filterData instanceof Array) {
			filters.push(filterData);
		} else if (targetData.hasOwnProperty("filter_json")
			&& targetData.filter_json instanceof Array
		) {
			filters.push(targetData.filter_json);
		} else if (targetData.hasOwnProperty("filter_json")) {
			const filterJson = JSON.parse(targetData.filter_json);
			if (filterJson.hasOwnProperty("filters") && filterJson.filters instanceof Array) {
				filterJson.filters.forEach(function (filter) {
					filters.push(JSON.stringify(filter));
				});
			}
		} else if (typeof filterData === 'string') {
			filters.push(filterData);
		}
	});
	return filters;
};

// Format app filters correctly
let failed_parent = false;
export const resolveDepartmentFilters = (filters = null) => {
	const result = JSON.parse(JSON.stringify(filters));
	result && result.forEach((filter, index) => {
		const target = filter?.[1];
		// Replace % with our value
		target?.value?.map(a => `${a}`).forEach((value, index) => {
			const tmpValue = value.replace(/%department./g, "").replace(/%/g, "");
			const tmpParts = tmpValue.split(".");
			const tmpKey = tmpParts[tmpParts.length - 1];
			const tmpParent = tmpParts.length > 1 ? tmpParts[0] : null;
			// console.log("tmpValue", tmpValue);
			// console.log("tmpParts", tmpParts);
			// console.log("tmpKey", tmpKey);
			// console.log("tmpParent", tmpParent);
			const tmpTarget = department.value?.[tmpParent];
			target.value[index] = tmpTarget?.data?.[tmpKey] ?? null;
		});
	});
	return result;
}
export const resolveAppFilters = (input_filters = null) => {
	return new Promise((resolve, reject) => {

		let failed_resolving = false;
		const promises = [];

		const filters = JSON.parse(JSON.stringify(input_filters ?? []));


		filters && filters.forEach((filter, index) => {
			const target = filter[1];
			// Make sure we target the correct key
			const tmpValue = JSON.parse(JSON.stringify((target.v ?? target.value ?? null)));
			delete target.v;
			// Make sure it is a array to work with
			if (!tmpValue || (tmpValue === "null")) {
				target.value = tmpValue;
			} else {
				target.value = !Array.isArray(tmpValue) ? [tmpValue] : tmpValue;
				target.value?.map(a => `${a}`).forEach((value, index) => {
					if (value.startsWith("%department.") && value.endsWith("%")) {
						// Resolved in later state
						return;
					} else if (value.startsWith("%") && value.endsWith("%")) {
						// Replace % with our value
						const tmpValue = value.replace(/%/g, "").replace(/%/g, "");
						const tmpParts = tmpValue.split(".");
						const tmpKey = tmpParts[tmpParts.length - 1];
						const tmpParent = tmpParts.length > 1 ? tmpParts[0] : null;
						// console.log("tmpValue", tmpValue);
						// console.log("tmpParts", tmpParts);
						// console.log("tmpKey", tmpKey);
						// console.log("tmpParent", tmpParent);

						// Check for variable existance
						if (tmpParent && env(`variables.${tmpParent}`) && !portalStore.fetching.includes(tmpParent)) {
							portalStore.fetching.push(tmpParent);
							// Listen for changes
							fetchRelated(tmpParent);
							promises.push(new Promise(res => {
								let promiseWatcher;
								// console.log("tmpParent",tmpParent);
								promiseWatcher = watch(
									() => portalStore[tmpParent],
									(parent_object) => {
										failed_parent = true;
										if (!parent_object) return;
										// Restart our current function based on new input
										resolveAppFilters(filters).then(res);
										promiseWatcher && promiseWatcher();
									},
									{
										immediate: true
									}
								);
							}));
						}

						const newValue = resolveValueParts(tmpKey, tmpParent);
						if (!newValue) {
							if ((tmpParent === "company") && isCompanyUndefinedAllowed.value) {
								delete filters[index];
								return;
							}
							failed_resolving = true;
							return;
						}
						target.value[index] = newValue;
						// Remove duplicates within target.value array
						target.value = [...new Set(target.value)].map(a => `${a}`);
					}
				});
			}
		});

		console.log("@TODO check functionality: resolveDepartmentFilters(filters)",resolveDepartmentFilters(filters), filters);
		if (!promises.length) {
			if (!failed_resolving || failed_parent) {
				return resolve(filters);
			} else {
				return resolveAppFilters(filters).then(resolve);
			}
		} else {
			if (!filters.filter(a => !!a).length) {
				return resolve([]);
			}
			Promise.all(promises).then(() => {
				resolve(filters);
			});
		}
	});
};

// Format the related filters correctly
export const resolveRelatedFilters = (related_filter_json) => {
	const filters = [];
	const relatedFilterJson = JSON.parse(related_filter_json ?? "{}");
	relatedFilterJson?.filters?.forEach(filter => {
		filters.push(JSON.stringify(filter));
	});
	return filters;
};
